import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { FaStamp } from 'react-icons/fa';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import { archiveDoc, sendToEmployee, sendToEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
}

const VerifyDoc: FC<IProps> = ({ id, docHolderId, buttonText, handlePreview, invalidate, size = 'sm' }: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Verify document
  const handleVerify = (docId: string) =>
    API.post(`documents/${docId}/approve`)
      .then(res => {
        const { message } = res.data;
        showModal({
          type: 'success',
          content: t('documents.table.verifiedDocSuccessMsg'),
          onSuccess: () => {
            if (message?.type === 'approve_sign')
              showModal({
                type: 'success',
                heading: t('documents.table.notificationSent'),
                content: message.body,
                successButton: (
                  <Button
                    btnType="primary"
                    onClick={() => {
                      hideModal();
                    }}
                  >
                    {t('common.ok')}
                  </Button>
                ),
              });

            if (message?.type === 'send_to_employee')
              sendToEmployee(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);

            if (message?.type === 'send_to_employer')
              sendToEmployer(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);

            if (message?.type === 'archive')
              archiveDoc(docHolderId, message.body, showModal, () => invalidateQueries([], invalidate), t);
          },
        });
        invalidateQueries([], invalidate);
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );

  // Confirm verification
  const confirmVerify = (docId: string) => {
    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmVerifyDocMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {t('documents.table.previewDoc')}
          </Button>
          <hr />
        </>
      ),
      onConfirm: () => handleVerify(docId),
      confirmLabel: t('documents.table.confirmVerifyDocLabel'),
    });
  };

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-verify"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="primary"
          size={size}
          onClick={() => confirmVerify(id)}
        >
          <FaStamp size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default VerifyDoc;
